import {
  Bus,
  ClipboardCheck,
  ClipboardList,
  FileSearch,
  FileText,
  LogIn,
  Receipt,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import heroTeg from '@/ui/assets/hero-landing.jpg';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';

/**
 * `/` — tela inicial do portal público.
 *
 * O que o programa é, o que o sistema faz, e por onde entra quem tem conta. Não
 * mostra número de estudantes nem estatística de atendimento: o protótipo trazia
 * "mais de 144 mil estudantes", e esse número **não existe em documento nenhum do
 * repositório** — estatística institucional sem fonte não se publica em portal de
 * governo (PRD, seção 1: nada de dado de exemplo).
 *
 * As três chamadas do hero levam às jornadas públicas do transportador —
 * autocadastro, acompanhamento da inscrição e consulta de credenciados —,
 * migradas do protótipo em 2026-09-21. Até então não havia chamada nenhuma aqui,
 * porque as telas não existiam: botão que leva a lugar nenhum é pior que botão
 * ausente. Elas leem dado mockado enquanto a API não existe (CLAUDE.md,
 * "Proibido", exceção de 2026-09-21).
 *
 * **Os rótulos não repetem os do rodapé de propósito.** "Acompanhar inscrição" e
 * "Consultar credenciados" já são links do rodapé institucional, que aparece em
 * toda página: dois links com o mesmo nome acessível quebram `getByRole` e
 * confundem quem usa leitor de tela. Mesma razão pela qual o cartão lá embaixo
 * diz "Entrar com a minha conta" e não "Entrar no sistema".
 *
 * **O hero não tem chamada de entrada.** Ela existia aqui e duplicava o botão do
 * topo da casca do portal (`CascaDoPortal.tsx`, "Entrar" / "Ir para a minha
 * área"), presente em toda página. Foi removida em 2026-09-21: a entrada no
 * sistema fica no topo e no cartão "Quem tem acesso ao sistema", no fim da
 * página. O hero é das jornadas públicas do transportador.
 */
const O_QUE_O_SISTEMA_FAZ = [
  {
    icone: FileText,
    titulo: 'Credenciamento',
    texto: 'Habilitação dos transportadores, com análise documental e termos digitais.',
  },
  {
    icone: Bus,
    titulo: 'Frota e operação',
    texto: 'Veículos, condutores, monitores, rotas e ordens de serviço.',
  },
  {
    icone: ClipboardCheck,
    titulo: 'Apuração mensal',
    texto: 'Medição do serviço prestado, ateste e demonstrativos.',
  },
  {
    icone: Receipt,
    titulo: 'Remuneração e cobranças',
    texto: 'Empenho, liquidação, pagamento e cobrança de penalidades.',
  },
];

export function PortalInicio() {
  return (
    <>
      <section className="relative overflow-hidden" aria-labelledby="titulo-inicial">
        <div className="absolute inset-0">
          <img
            src={heroTeg}
            alt=""
            role="presentation"
            className="h-full w-full object-cover"
            loading="eager"
          />
          {/*
           * Véu institucional: garante contraste do texto sobre qualquer foto.
           * O véu é `--hero-overlay`, não `--primary`, pelo mesmo motivo que em
           * `ui/PortalHero.tsx`: ele é translúcido, e o par véu/texto precisa ser
           * medido junto em cada tema. Usar `primary` aqui e
           * `hero-overlay-foreground` no texto descasaria os dois no tema de alto
           * contraste, onde o véu é amarelo e o texto, preto.
           */}
          <div className="absolute inset-0 bg-linear-to-r from-hero-overlay/95 via-hero-overlay/90 to-hero-overlay/75" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
          <div className="max-w-3xl space-y-5 animate-fade-in">
            <p className="flex items-center gap-2 text-sm font-medium text-hero-overlay-foreground">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
              Prefeitura de São Paulo · Secretaria Municipal de Mobilidade Urbana e Transporte
            </p>

            <h1
              id="titulo-inicial"
              className="text-3xl font-bold leading-tight tracking-tight text-hero-overlay-foreground sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none"
            >
              Transporte Escolar Gratuito
            </h1>

            <p className="max-w-2xl text-lg leading-relaxed text-hero-overlay-foreground">
              O TEG garante o transporte de estudantes da rede municipal de ensino. Este é o sistema
              que gerencia o credenciamento dos transportadores, a frota, as ordens de serviço, a
              apuração mensal e a remuneração do serviço prestado.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild size="lg" variant="secondary" className="gap-2">
                <Link to="/credenciados/autocadastro">
                  <ClipboardList className="h-5 w-5" aria-hidden="true" />
                  Fazer o AutoCadastro
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="gap-2 border-hero-overlay-foreground/50 bg-hero-overlay-foreground/10 text-hero-overlay-foreground hover:bg-hero-overlay-foreground/20 hover:text-hero-overlay-foreground"
              >
                <Link to="/credenciados/acompanhamento">
                  <FileSearch className="h-5 w-5" aria-hidden="true" />
                  Acompanhar a minha inscrição
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="gap-2 border-hero-overlay-foreground/50 bg-hero-overlay-foreground/10 text-hero-overlay-foreground hover:bg-hero-overlay-foreground/20 hover:text-hero-overlay-foreground"
              >
                <Link to="/dados-publicos/credenciados">
                  <Search className="h-5 w-5" aria-hidden="true" />
                  Consultar a lista de credenciados
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16" aria-labelledby="titulo-o-que-faz">
        <div className="mx-auto max-w-7xl space-y-8 px-4 sm:px-6">
          <div className="max-w-2xl space-y-2">
            <h2 id="titulo-o-que-faz" className="text-2xl font-bold tracking-tight">
              O que o sistema gerencia
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Os módulos são entregues por fase, cada um completo: telas, regras, filtros e escopo
              por perfil, com validação da Secretaria antes de seguir para o próximo.
            </p>
          </div>

          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {O_QUE_O_SISTEMA_FAZ.map(({ icone: Icone, titulo, texto }) => (
              <li key={titulo}>
                <Card className="h-full">
                  <CardHeader className="space-y-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icone className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <CardTitle className="text-base">{titulo}</CardTitle>
                    <CardDescription className="leading-relaxed">{texto}</CardDescription>
                  </CardHeader>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border bg-muted/40 py-14" aria-labelledby="titulo-acesso">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Card className="mx-auto max-w-3xl">
            <CardHeader>
              <CardTitle id="titulo-acesso" className="text-lg">
                Quem tem acesso ao sistema
              </CardTitle>
              <CardDescription className="leading-relaxed">
                O acesso é feito com conta própria e segundo fator de autenticação. Servidores da
                Secretaria e das diretorias regionais entram na área de operação; as empresas
                credenciadas, no portal do transportador.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/*
                Rótulo diferente do botão do topo de propósito: dois links com o
                mesmo nome acessível na mesma página fazem quem usa leitor de tela
                ouvir a lista duplicada, e deixam `getByRole` ambíguo no teste.
              */}
              <Button
                asChild
                variant="outline"
                className="gap-2 border-primary-foreground/50 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
              >
                <Link to="/login">
                  <LogIn className="h-4 w-4" aria-hidden="true" />
                  Entrar com a minha conta
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  );
}

export default PortalInicio;
